import Grid from '@mui/material/Grid';
import Skeleton from '@mui/material/Skeleton';
import classNames from 'classnames';
import { Fragment, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import Container from '/@/components/container';
import NewMetrics from '/@/components/new-metrics';
import {
    useAllSoyMultipliers,
    useSoyMultiplier,
    useSoyMultipliers,
    useSoyMultipliersZones,
} from '/@/hooks/soy-multipliers';
import { _ } from '/@/locale';
import { setCatalogComparisons as setCatalogComparisonsAction } from '/@/store/catalog/actions';
import { getCatalogComparisons } from '/@/store/catalog/selectors';
import { getCatalogComparisonChoicesMultipliers, getSoyBrands } from '/@/store/multipliers/selectors';
import { getSessionCountry, getUser } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import CatalogHeader from '/@/v3/catalog/catalog-header';
import ToggleGroup from '/@/v3/components/forms/toggle-group';
import { Multipliers } from '/@/v3/components/metrics';
import Title from '/@/v3/components/title';
import styles from './styles.module.css';

const SoyMultipliersDetail = ({ zoneId, profileId }) => {
    const dispatch = useDispatch();
    const history = useNavigate();
    const brands = useSelector(getSoyBrands);
    const country = useSelector(getSessionCountry);
    const multiplierZonesQuery = useSoyMultipliersZones({ country });
    const multiplierZone = useMemo(
        () => (multiplierZonesQuery.data?.results || []).find((z) => z.id === zoneId),
        [multiplierZonesQuery.data?.results, zoneId]
    );

    const user = useSelector(getUser);
    const comparisons = useSelector((state) => getCatalogComparisons(state, 'soy', profileId));
    const { data: allItems } = useAllSoyMultipliers(zoneId);
    const comparisonChoices = useMemo(() => {
        return getCatalogComparisonChoicesMultipliers(allItems || [], profileId, multiplierZone);
    }, [allItems]);

    useEffect(() => {
        setCatalogComparisons([]);
    }, []);

    const setCatalogComparisons = bindActionCreators(setCatalogComparisonsAction, dispatch);
    const zoneName = multiplierZone ? multiplierZone.name : '';
    const { data: item, isValidating: isLoading } = useSoyMultiplier(profileId);

    const ids = [...comparisons, profileId];
    const { data: items } = useSoyMultipliers(ids);

    return (
        <Fragment>
            <CatalogHeader brand={brands[0]} onGoBack={() => history(reverse('SoyMultipliers'))} />
            <Container className={styles.wrapper}>
                {isLoading ? (
                    <Grid container gap={2}>
                        {Array.from({ length: 3 }).map((_, index) => (
                            <Grid key={index} item xs={12}>
                                <Skeleton variant='text' sx={{ fontSize: '2rem' }} />
                            </Grid>
                        ))}
                        <Grid item xs={12}>
                            <Skeleton variant='rectangular' width='100%' height={300} />
                        </Grid>
                    </Grid>
                ) : (
                    <Fragment>
                        <Title bolder className={styles.title}>
                            {item.product.name}
                            <span className={classNames(styles.subtitle, styles.titleZone)}>{_(zoneName)}</span>
                        </Title>
                        <Title bolder className={styles.subtitle}>
                            {_(item.product.shortDescription)}
                        </Title>
                        {comparisonChoices && comparisonChoices.length && (
                            <div className={styles.comparisons}>
                                <Title color='secondary' className={styles.titleComparisons} bolder>
                                    {_('Compará con otros productos')} ({_('Seleccioná hasta 3')})
                                </Title>
                                <ToggleGroup
                                    type='checkbox'
                                    max={3}
                                    value={comparisons}
                                    options={comparisonChoices}
                                    onChange={setCatalogComparisons}
                                />
                            </div>
                        )}
                        <div className={styles.multipliers}>
                            <Title className={styles.titleTable} color='primary' size='lg' bolder>
                                {_('Información técnica para la multiplicación de semillas')}
                            </Title>
                            {items ? (
                                <Multipliers highlight profiles={items} user={user} />
                            ) : (
                                <Skeleton variant='rectangular' width='100%' height={300} />
                            )}
                        </div>
                        <div className={styles.features}>
                            <Title className={styles.titleTable} color='primary' size='lg' bolder>
                                {_('Características')}
                            </Title>
                            {items ? (
                                <NewMetrics
                                    highlight
                                    title={_('Características')}
                                    dataKey='features'
                                    products={items.map((item) => {
                                        return {
                                            ...item,
                                            ...item.product,
                                            features: item.product.features.map((f) => {
                                                return {
                                                    id: f.feature.id,
                                                    name: f.feature.name,
                                                    order: f.feature.order,
                                                    value: f.value,
                                                };
                                            }),
                                        };
                                    })}
                                    brand={brands[0]} //TODO: BRAND
                                    country={country}
                                    catalog={false}
                                />
                            ) : (
                                <Skeleton variant='rectangular' width='100%' height={300} />
                            )}
                        </div>
                        <div className={styles.defensiveProfile}>
                            <Title className={styles.titleTable} color='primary' size='lg' bolder>
                                {_('Perfil fitosanitario')}
                            </Title>
                            {items ? (
                                <NewMetrics
                                    highlight
                                    dots
                                    title={_('Perfil defensivo')}
                                    dataKey='metrics'
                                    products={items.map((item) => {
                                        return {
                                            ...item,
                                            ...item.product,
                                            metrics: item.product.metrics.map((m) => {
                                                return {
                                                    ...m.metric,
                                                    value: m.value,
                                                };
                                            }),
                                        };
                                    })}
                                    brand={brands[0]} //TODO: BRAND
                                    country={country}
                                    catalog={false}
                                />
                            ) : (
                                <Skeleton variant='rectangular' width='100%' height={300} />
                            )}
                        </div>
                    </Fragment>
                )}
            </Container>
        </Fragment>
    );
};

export default SoyMultipliersDetail;
